import type { CodeableConcept, Coding, Observation } from '@medplum/fhirtypes'
import { OBSERVATION_INTERPRETATION_SYSTEM } from '#src/codeSystems.ts'
import { componentValue } from '#src/models/observationComponents.ts'
import {
  calculationMethod,
  type DerivationRule,
} from '#src/observation-logic/derivations/rules.ts'
import {
  ORTHOSTATIC_CONSENSUS_SOURCE,
  ORTHOSTATIC_DIASTOLIC_CHANGE_CODING,
  ORTHOSTATIC_HEART_RATE_CHANGE_CODING,
  ORTHOSTATIC_READINGS,
  ORTHOSTATIC_SYSTOLIC_CHANGE_CODING,
} from '#src/terminology/vitals.ts'

// The change on standing that orthostatic hypotension is judged by, from
// blood pressure and heart rate taken lying, sitting and standing.

const BASELINE_NOTE =
  'Measured against lying; when no lying value is recorded, against sitting. The consensus definition compares lying with standing within 3 minutes.'

type Measure = 'systolic' | 'diastolic' | 'heartRate'

function changeRule(
  id: string,
  target: Coding,
  measure: Measure,
  unit: string,
  measureLabel: string
): DerivationRule {
  const lying = ORTHOSTATIC_READINGS.lying[measure]
  const sitting = ORTHOSTATIC_READINGS.sitting[measure]
  const standing = ORTHOSTATIC_READINGS.standing[measure]
  return {
    id,
    target,
    inputs: [lying, sitting, standing],
    optional: [lying, sitting],
    sameVisit: true,
    category: 'vital-signs',
    method: calculationMethod(id, `Standing − lying ${measureLabel}`, {
      formula: `standing ${measureLabel} − lying ${measureLabel} (sitting when lying is blank)`,
      citation: ORTHOSTATIC_CONSENSUS_SOURCE.citation,
      url: ORTHOSTATIC_CONSENSUS_SOURCE.url,
      note: BASELINE_NOTE,
    }),
    compute: (inputs) => {
      const upright = inputs.quantity(standing, unit)
      const baseline =
        inputs.quantity(lying, unit) ?? inputs.quantity(sitting, unit)
      if (upright === null || baseline === null) {
        return null
      }
      return { kind: 'number', value: upright - baseline, unit }
    },
  }
}

export const ORTHOSTATIC_SYSTOLIC_CHANGE_RULE = changeRule(
  'orthostatic-systolic-change',
  ORTHOSTATIC_SYSTOLIC_CHANGE_CODING,
  'systolic',
  'mm[Hg]',
  'systolic'
)

export const ORTHOSTATIC_DIASTOLIC_CHANGE_RULE = changeRule(
  'orthostatic-diastolic-change',
  ORTHOSTATIC_DIASTOLIC_CHANGE_CODING,
  'diastolic',
  'mm[Hg]',
  'diastolic'
)

export const ORTHOSTATIC_HEART_RATE_CHANGE_RULE = changeRule(
  'orthostatic-heart-rate-change',
  ORTHOSTATIC_HEART_RATE_CHANGE_CODING,
  'heartRate',
  '{beats}/min',
  'heart rate'
)

export const ORTHOSTATIC_DERIVATIONS: readonly DerivationRule[] = [
  ORTHOSTATIC_SYSTOLIC_CHANGE_RULE,
  ORTHOSTATIC_DIASTOLIC_CHANGE_RULE,
  ORTHOSTATIC_HEART_RATE_CHANGE_RULE,
]

// Orthostatic hypotension by the consensus definition: a sustained drop of
// at least 20 mmHg systolic or 10 mmHg diastolic on standing. Undefined
// until both a baseline and a standing pressure are recorded.
export function hasOrthostaticHypotension(
  systolicChange: number | undefined,
  diastolicChange: number | undefined
): boolean | undefined {
  if (systolicChange === undefined && diastolicChange === undefined) {
    return undefined
  }
  return (
    (systolicChange !== undefined && systolicChange <= -20) ||
    (diastolicChange !== undefined && diastolicChange <= -10)
  )
}

function numberPart(
  observation: Pick<Observation, 'component'>,
  coding: Coding
): number | undefined {
  const value = componentValue(observation, coding)
  return typeof value === 'number' ? value : undefined
}

// The panel's `Observation.interpretation`: orthostatic hypotension or not,
// from its calculated changes. Undefined until a change can be calculated.
export function orthostaticInterpretation(
  observation: Pick<Observation, 'component'>
): CodeableConcept[] | undefined {
  const positive = hasOrthostaticHypotension(
    numberPart(observation, ORTHOSTATIC_SYSTOLIC_CHANGE_CODING),
    numberPart(observation, ORTHOSTATIC_DIASTOLIC_CHANGE_CODING)
  )
  if (positive === undefined) {
    return undefined
  }
  return [
    {
      coding: [
        positive
          ? {
              system: OBSERVATION_INTERPRETATION_SYSTEM,
              code: 'A',
              display: 'Abnormal',
            }
          : {
              system: OBSERVATION_INTERPRETATION_SYSTEM,
              code: 'N',
              display: 'Normal',
            },
      ],
      text: positive ? 'orthostatic hypotension' : 'no orthostatic hypotension',
    },
  ]
}
